import React, { FC, Fragment, useCallback, useState } from 'react';
import {
    Alert,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { ActionFilter, LabAction } from 'models/action';
import { useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getCategoriesTemplate, getGroupsTemplate, getLabActionsTemplate } from 'apollo/templates/action';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import ActionsPageActions from './actions-page-actions.component';
import ActionItem from './action-item.component';
import AddActionDialog from './add-action-dialog/add-action-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import EditActionDialog from './edit-action-dialog/edit-action-dialog.component';
import { OrderBy, OrderDirection } from 'models/search-result';

const Actions: FC = () => {
    const { t: translate } = useTranslation([
        'common',
        'actions-materials',
        'orders',
        'lab-settings',
        'manage-actions',
    ]);
    const [actions, setActions] = useState<LabAction[]>([]);
    const [filters, setFilters] = useState<ActionFilter>({
        freetext: '',
        category: '',
        active: true,
        group: '',
        productionPlanTemplate: 'ALL',
        productionValueTemplate: 'ALL',
    });
    const [actionToEdit, setActionToEdit] = useState<LabAction>({} as LabAction);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const [snackbarOpen, setSnackbarOpen] = useState({ addedAction: false, updatedAction: false });
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: 'description',
        orderDirection: OrderDirection.asc,
    });
    const { loading: labActionsLoading, refetch } = useQuery(getLabActionsTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
        variables: {
            filters: filters,
            ...orderBy,
        },
        onError(error) {
            showBoundary(error);
        },
        onCompleted({ labActions }) {
            setActions(labActions);
        },
    });

    const { data: { categories } = {}, loading: categoriesLoading } = useQuery(getCategoriesTemplate, {
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { groups } = {}, loading: groupsLoading } = useQuery(getGroupsTemplate, {
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFilterChange = useCallback(
        debounce(
            ({ freetext, category, active, group, productionPlanTemplate, productionValueTemplate }: ActionFilter) => {
                setFilters({
                    freetext: freetext,
                    category: category,
                    group: group,
                    active: active,
                    productionPlanTemplate: productionPlanTemplate,
                    productionValueTemplate: productionValueTemplate,
                });
            },
            300,
        ),
        [],
    );

    const handleEditAction = (action: LabAction) => {
        setActionToEdit(action);
        setEditDialogOpen(true);
    };

    // A new column starts ascending, clicking the already active column flips the direction.
    const setOrderOnField = (field: string) => {
        const isActiveColumnAscending = orderBy.orderBy === field && orderBy.orderDirection === OrderDirection.asc;
        setOrderBy({
            orderBy: field,
            orderDirection: isActiveColumnAscending ? OrderDirection.desc : OrderDirection.asc,
        });
    };

    return (
        <Fragment>
            <Box mb={1}>
                <ActionsPageActions
                    onNewActionClick={() => setAddDialogOpen(true)}
                    onFilterChange={handleFilterChange}
                    groups={groups}
                />
            </Box>
            {labActionsLoading || categoriesLoading || groupsLoading ? (
                <Loader />
            ) : (
                <Fragment>
                    {!actions.length ? (
                        <Box mt={2}>
                            <Alert severity="info">
                                {filters.active && translate('lab-settings:NO_ACTIVE_ACTIONS')}
                                {!filters.active && translate('lab-settings:NO_INACTIVE_ACTIONS')}
                                {filters === null && translate('lab-settings:NO_ACTIONS')}
                            </Alert>
                        </Box>
                    ) : (
                        <Paper variant="outlined">
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    onClick={() => setOrderOnField('actionCode')}
                                                    active={orderBy.orderBy === 'actionCode'}
                                                    direction={orderBy.orderDirection}
                                                >
                                                    {translate('actions-materials:CODE')}
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    onClick={() => setOrderOnField('description')}
                                                    active={orderBy.orderBy === 'description'}
                                                    direction={orderBy.orderDirection}
                                                >
                                                    {translate('common:DESCRIPTION')}
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                {translate('manage-actions:DENTHUB_RESTORATION_TYPE')}
                                            </TableCell>
                                            <TableCell>{translate('lab-settings:GROUP')}</TableCell>
                                            <TableCell>{translate('lab-settings:MATERIAL_MACROS')}</TableCell>
                                            <TableCell>{translate('orders:CATEGORY')}</TableCell>
                                            <TableCell>{translate('actions-materials:PRODUCTION_PLAN')}</TableCell>
                                            <TableCell>{translate('actions-materials:VALUE_TEMPLATE')}</TableCell>
                                            <TableCell>{translate('actions-materials:ADDED_DATE')}</TableCell>
                                            {!filters.active && (
                                                <TableCell>{translate('actions-materials:INACTIVE_DATE')}</TableCell>
                                            )}{' '}
                                            <TableCell align="center">{translate('common:STATUS')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {actions.map((action, index) => (
                                            <ActionItem
                                                key={action._id || index}
                                                action={action}
                                                onActionRowClick={handleEditAction}
                                                showInactiveDateCell={!filters.active}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                    <AddActionDialog
                        dialogOpen={addDialogOpen}
                        onCloseDialog={() => setAddDialogOpen(false)}
                        categories={categories}
                        groups={groups}
                        onAddAction={(action: LabAction) => {
                            setSnackbarOpen({ ...snackbarOpen, addedAction: true });
                            setActions([...actions, action]);
                        }}
                    />
                    <EditActionDialog
                        dialogOpen={editDialogOpen}
                        onCloseDialog={() => setEditDialogOpen(false)}
                        categories={categories}
                        groups={groups}
                        actionToEdit={actionToEdit}
                        onEditAction={() => {
                            setSnackbarOpen({ ...snackbarOpen, updatedAction: true });
                            refetch();
                        }}
                    />
                    {snackbarOpen.addedAction ? (
                        <AlertSnackbar
                            type="success"
                            message={translate('lab-settings:ACTION_ADDED')}
                            onClose={() => setSnackbarOpen({ ...snackbarOpen, addedAction: false })}
                        />
                    ) : undefined}
                    {snackbarOpen.updatedAction ? (
                        <AlertSnackbar
                            type="success"
                            message={translate('lab-settings:ACTION_UPDATED')}
                            onClose={() => setSnackbarOpen({ ...snackbarOpen, updatedAction: false })}
                        />
                    ) : undefined}
                </Fragment>
            )}
        </Fragment>
    );
};

export default Actions;
